// src/pages/EntertainmentPage.js
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaHome, FaShoppingCart, FaBars, FaTimes, FaChevronDown,
  FaStar, FaMusic, FaMicrophoneAlt, FaTheaterMasks, FaHeart, FaUserCircle
} from "react-icons/fa";
import { useCart } from "../cart/CartContext";
import { useAuth } from "../auth/AuthContext";
import "../styles/entertainment.css";

// Fallback data (used if CMS file missing)
const FALLBACK = {
  hero: {
    title: "Entertainment, Artists & Acts",
    subtitle:
      "From DJ nights to folk dances to celebrity appearances — book the perfect entertainment for your celebration.",
    image: ""
  },
  categories: [
    { icon: "music", name: "Music & Bands" },
    { icon: "mic", name: "Anchors & Hosts" },
    { icon: "masks", name: "Traditional & Folk" },
    { icon: "heart", name: "Special Acts" },
  ],
  items: [
    {
      id: "dj-night",
      name: "DJ Night",
      type: "Music",
      highlights: ["High Energy", "Custom Playlist"],
      desc: "A professional DJ with lights & sound system.",
      price: 15000,
      status: "available",
      image: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=500&h=300&fit=crop"
    },
    {
      id: "folk-dance",
      name: "Folk Dance Group",
      type: "Traditional",
      highlights: ["Authentic", "Colorful Costumes"],
      desc: "Experience vibrant folk performances.",
      price: 25000,
      status: "available",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTsV1CQGC8qrrHF8h2rF1TXDPnM6O8sJoVG0Q&s"
    }
  ]
};

const iconFor = (key) => {
  switch (key) {
    case "music": return <FaMusic />;
    case "mic": return <FaMicrophoneAlt />;
    case "masks": return <FaTheaterMasks />;
    case "heart": return <FaHeart />;
    default: return <FaStar />;
  }
};

const servicesLinks = [
  { to: "/wedding", label: "Wedding Planning" },
  { to: "/design", label: "Design & Decoration" },
  { to: "/destination", label: "Destination Wedding Venues" },
  { to: "/entertainment", label: "Entertainment" },
  { to: "/others", label: "Custom Events" },
];

function EntertainmentPage() {
  const { cart, addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addedId, setAddedId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const dropdownTimeout = useRef(null);

  const handleProfileClick = (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
    } else {
      navigate("/profile");
    }
  };

  const [data, setData] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  // Load from CMS JSON (with fallback)
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch("/content/entertainment.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancel && json) {
          setData({
            ...FALLBACK,
            ...json,
            categories: json.categories?.length ? json.categories : FALLBACK.categories,
            items: json.items?.length ? json.items : FALLBACK.items,
          });
        }
      } catch {
        /* use FALLBACK */
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  function handleAddToCart(item) {
    if (!user) {
      alert("Please login to add items to cart");
      navigate("/");
      return;
    }
    const isAvailable = (item.status || "available") !== "unavailable";
    const hasNumericPrice = typeof item.price === "number" && item.price > 0;
    if (!isAvailable || !hasNumericPrice) return;
    addToCart(item);
    setAddedId(item.id);
    setTimeout(() => setAddedId(null), 900);
  }

  const toggleNav = () => {
    setMenuOpen(!menuOpen);
    document.body.classList.toggle("nav-open", !menuOpen);
  };

  return (
    <div className="ent-page">
      {/* NAVBAR */}
      <header className="ent-navbar">
        <div className="ent-navbar-container">
          <div className="ent-navbar-brand" onClick={() => navigate("/")} style={{ fontFamily: "'Great Vibes', cursive" }}>
           Parivar
          </div>

          <nav className="ent-center-nav">
            <Link to="/" className="ent-nav-link"><FaHome /> Home</Link>

            <div
              className="ent-dropdown"
              onMouseEnter={() => { clearTimeout(dropdownTimeout.current); setDropdown(true); }}
              onMouseLeave={() => { dropdownTimeout.current = setTimeout(() => setDropdown(false), 200); }}
            >
              <button className="ent-dropdown-btn">Services <FaChevronDown /></button>
              <div className={`ent-dropdown-menu ${dropdown ? "show" : ""}`}>
                {servicesLinks.map((item) => (
                  <Link key={item.to} to={item.to} className="ent-dropdown-link" onClick={() => setDropdown(false)}>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* RIGHT ICONS */}
          <div className="ent-nav-right">
            <a href="/profile" onClick={handleProfileClick} className="ent-cart-link" aria-label="Profile"><FaUserCircle /></a>
            <Link to="/cart" className="ent-cart-link" aria-label="Cart">
              <FaShoppingCart />
              {cart.length > 0 && <span className="ent-cart-badge">{cart.length}</span>}
            </Link>
          </div>

          <div className="ent-mobile-menu-icon" onClick={toggleNav}>
            {menuOpen ? <FaTimes /> : <FaBars />}
          </div>
        </div>
      </header>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div className="ent-sidebar-overlay" onClick={toggleNav}>
          <div className="ent-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="ent-sidebar-header"><span>Menu</span><FaTimes onClick={toggleNav} /></div>
            <div className="ent-sidebar-link" onClick={() => { navigate("/"); toggleNav(); }}><FaHome /> Home</div>
            <div className="ent-sidebar-link" onClick={() => setDropdown(!dropdown)}><FaChevronDown /> Services</div>
            {dropdown && (
              <div className="ent-sidebar-submenu">
                {servicesLinks.map((link) => (
                  <div key={link.to} className="ent-sidebar-submenu-item" onClick={() => { navigate(link.to); toggleNav(); }}>
                    {link.label}
                  </div>
                ))}
              </div>
            )}
            <div className="ent-sidebar-link" onClick={() => { 
              if (!user) {
                navigate("/login");
              } else {
                navigate("/profile");
              }
              toggleNav(); 
            }}><FaUserCircle /> Profile</div>
            <div className="ent-sidebar-link" onClick={() => { navigate("/cart"); toggleNav(); }}>
              <span className="ent-cart-icon-wrapper">
                <FaShoppingCart /> {cart.length > 0 && <span className="ent-cart-badge">{cart.length}</span>}
              </span>
              <span>Cart</span>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="ent-hero" style={data.hero?.image ? { backgroundImage: `url(${data.hero.image})` } : {}}>
        <div className="ent-hero-inner">
          <h1>{data.hero?.title || FALLBACK.hero.title}</h1>
          <p>{data.hero?.subtitle || FALLBACK.hero.subtitle}</p>
          <div className="ent-categories">
            {(data.categories || []).map((cat, idx) => (
              <div key={idx} className="ent-category">{iconFor(cat.icon)} {cat.name}</div>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="ent-grid">
        {loading && <div className="ent-loading">Loading…</div>}
        {!loading && (data.items || []).map((option) => {
          const isAvailable = (option.status || "available") !== "unavailable";
          const hasNumericPrice = typeof option.price === "number" && option.price > 0;
          const priceDisplay = hasNumericPrice ? `₹${option.price.toLocaleString()}` : (option.price_label || "Request Quote");
          return (
            <div key={option.id} className={`ent-card ${!isAvailable ? "unavailable" : ""}`}>
              <img src={option.image} alt={option.name} loading="lazy" />
              <div className="ent-info">
                <h3>
                  {option.name}
                  <span className={`pkg-status ${isAvailable ? "available" : "unavailable"}`}>
                    {isAvailable ? "Available" : "Unavailable"}
                  </span>
                </h3>
                <div className="ent-type">{option.type}</div>
                {!!option.highlights?.length && (
                  <div className="ent-tags">
                    {option.highlights.map((tag, i) => <span key={i} className="ent-tag">{tag}</span>)}
                  </div>
                )}
                <div className="ent-desc">{option.desc}</div>

                <div className="ent-card-footer">
                  <div className="ent-price">{priceDisplay}</div>

                  {isAvailable && !hasNumericPrice ? (
                    <button className="ent-add-btn outline" onClick={() => navigate("/others")}>
                      Request Quote
                    </button>
                  ) : (
                    <button
                      className={`ent-add-btn ${addedId === option.id ? "added" : ""}`}
                      onClick={() => handleAddToCart(option)}
                      disabled={!isAvailable || addedId === option.id || !hasNumericPrice}
                      title={!isAvailable ? "Unavailable" : !hasNumericPrice ? "Price not set" : "Add to cart"}
                    >
                      {!isAvailable ? "Unavailable" : addedId === option.id ? "Added!" : "Add to Cart"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* HOW TO BOOK */}
      <section className="ent-how">
        <FaStar className="ent-how-icon" />
        <h2>How to Book Your Entertainment</h2>
        <p>
          1) Browse & select your act · 2) Click <b>Add to Cart</b> · 3) We call for
          customizations and scheduling · 4) Enjoy the show!
        </p>
      </section>
    </div>
  );
}

export default EntertainmentPage;
